import { orchestrator } from '../clients.ts'
import { keys } from '../keys.ts'
import { createQuery } from '../query.ts'

/**
 * 🔴 **정본 계약에 «아직» 없는 경로**(서버 `views/monitoring.py` · 대장 등재 대기) — `GET /monitoring/trays`.
 *
 * 트레이(C · A · L)별 웰 · 개수 · 자리, 원심 로터 · 어셈블러 · 염색기 안 챔버, 챔버별 자리(시작 트레이 → 장비 → 목표 트레이)를
 * 한 응답으로 받는다(eric 2026-10-07 「C 트레이 A 트레이 L 트레이 하나하나 모니터링 페이지에서 볼 수 있게」).
 * 🔴 **자리는 되돌아가지 않는다** — 서버가 실행 스텝 기록의 «완료 최대 스텝»으로 센다(종전 화면의 `temporaryChamberFlow` 는
 *    스텝을 칸 수로 나눈 나머지로 지어 내 결과 트레이 ↔ 시작 트레이를 왔다 갔다 했다).
 * 🔴 값마다 출처(`source`)가 있다 — `server` 표에 있는 사실 · `derived` 서버 사실에서 셈 · `mock` 서버가 아직 모르는 값.
 * ⚠ 폴링하지 않는다(DP-01) — 장비 상태 · 시료 단계 · 작업 상태 · 스텝 완료 이벤트가 이 키를 무효화한다(`stream/bridge.ts`).
 */
export type TraySource = 'server' | 'derived' | 'mock'
export type TrayName = 'C' | 'A' | 'L'

export interface TrayWellView {
  present: boolean
  sample_id: string | null
  sample_ref: string | null
  reference: boolean
  stage: string | null
  status: string | null
  source: TraySource
}

export interface TrayView {
  tray: TrayName
  role: string
  labware: string
  capacity: number
  count: number
  location: {
    module_type: string
    module_id: string | null
    point_id: string
    in_transit: boolean
    source: TraySource
  }
  counterweight: { present: boolean; where: string; source: TraySource } | null
  wells: Readonly<Record<string, TrayWellView>>
  /** 그 트레이의 가장 최근 비전 검사(서버 스텝 기록 `inspect` 행 · 정본 `SQ-CYCLE-09`) — 없으면 `null`(검사 기록 없음 · 지어내지 않는다) */
  inspected?: TrayInspection | null
}

export type TrayInspectionStatus = 'passed' | 'failed' | 'running'

export interface TrayInspection {
  status: TrayInspectionStatus
  key: string
  at: string | null
}

export interface TrayChamberView {
  well: string
  sample_id: string | null
  sample_ref: string | null
  reference: boolean
  tray: TrayName | null
  /** `tray` · `rotor_slot` · `tilt_stage` · `shaker` */
  place: string
  slot: number | null
  module_id: string | null
  /**
   * 그 공정이 이 챔버를 쥐고 있다(싣기 · 회전 · 꺼내기 · 틸트 · 염색).
   * ⚠ **원심은 «로터에 실린 챔버 전부»가 참이다**(서버 `tray_view._walk_centrifuge` · 시험 `test_tray_view.py` 가 그렇게 건다) —
   *   꺼낼 때 «지금 어느 챔버»인지는 이 칸으로 못 가린다. 그 하나는 `centrifuge.active_well` 이 말한다(2026-10-11).
   */
  active: boolean
  stage: string | null
  status: string | null
  /** 지나온 길(`C→rotor` · `rotor→A` · `A→tilt` · `tilt→L`) — 앞으로만 는다 */
  path: readonly string[]
  source: TraySource
}

interface ModuleTrayView {
  module_id: string
  state: string
  progress: number
  step: number | null
  total: number | null
  running_this_batch: boolean
  source: TraySource
}

export interface MonitoringTraysView {
  batch: {
    batch_id: string
    name: string | null
    status: string
    started: boolean
    stage: string
  } | null
  generated_at: string
  trays: readonly TrayView[]
  centrifuge:
    | (ModuleTrayView & {
        rotor: readonly {
          slot: number
          well: string
          sample_id: string | null
          source: TraySource
        }[]
        spinning: boolean
        /**
         * 원심이 «지금 걸음»에서 다루는 챔버 하나(시작했으나 안 끝난 스텝의 웰 · 회전 · 평형추 · 점검이면 `null`) — 어셈블러 `active_well` 과 같은 칸.
         * ⚠ 옛 판(2026-10-11 전 dev2)은 키째 안 싣는다 — 그때 그리퍼는 «다루는 중» 후보가 하나일 때만 따라간다(`centGripperModel`).
         */
        active_well?: string | null
        /**
         * 평형추(R″)의 자리 — `where` 는 `C`(트레이 RSLOT) · `gripper`(3축이 쥐고 감) · `rotor`.
         * 스텝 이름(`c41_ref_pick_cw` · `c42_ref_place_cw_s3` · `c48_cw_return` · `c49_cw_station`)을 걸은 판(dev2 2026-10-08)은
         * `slot`(로터 칸) · `to`(쥐고 가는 곳 `rotor` · `C`) · `target_slot`(지금 놓으러 가는 칸) · `active`(지금 다룸)를 더 싣는다 — 옛 판은 안 싣는다
         */
        counterweight: {
          where: string
          source: TraySource
          slot?: number | null
          to?: string | null
          target_slot?: number | null
          active?: boolean
        }
      })
    | null
  assembler:
    | (ModuleTrayView & {
        active_well: string | null
        at: string | null
        /** 지금 UV 노출 스텝(`expose_uv`)이 도는가 — 서버 스텝 기록 근거(장비 텔레메트리에 UV 세기가 없다 · 2026-10-07) */
        uv?: boolean
        uv_well?: string | null
        /**
         * 지금 시작한 걸음의 이름(계획 키 `a03_tc_pick_a1.grip` 의 `tc_pick`) · 그 원자(`approach` · `descend` · `grip` · `release` · `ascend`) ·
         * 이 작업에서 끝낸 TC 집기 수. dev2 2026-10-08 판부터 싣는다 — 옛 판은 없다(그림은 그리퍼를 비켜 세운다)
         */
        action?: string | null
        action_atom?: string | null
        tc_picked?: number
      })
    | null
  staining:
    | (ModuleTrayView & {
        active_wells: readonly string[]
        incubation: Record<string, unknown> | null
      })
    | null
  chambers: readonly TrayChamberView[]
  /** 이 배치의 비전 검사 전부(작업 경계 · 모듈이 판정 · 실패는 에러 코드) — 시각 차례 */
  inspections?: readonly {
    key: string
    module_id: string
    module_type: string
    kind: 'inlet' | 'rotor' | 'after_spin' | 'outlet' | 'other'
    tray: TrayName | null
    status: TrayInspectionStatus
    error_code: string | null
    at: string | null
    source: TraySource
  }[]
  /** 목 응답이면 그 국면(`before` · `centrifuge` · `assembler` · `staining` · `done`) */
  mock?: string
}

export interface MonitoringTraysArgs {
  /** 없으면 서버가 도는 배치(없으면 가장 최근 배치)를 고른다 */
  batchId?: string | undefined
  skip?: boolean
}

export const useMonitoringTrays = createQuery<MonitoringTraysArgs, MonitoringTraysView>({
  key: ({ batchId }) => keys.monitoringTrays.detail(batchId ?? 'current'),
  fetch: ({ batchId }) =>
    orchestrator.request<MonitoringTraysView>({
      method: 'GET',
      path: '/monitoring/trays',
      ...(batchId === undefined ? {} : { query: { batch_id: batchId } }),
    }),
})

/** 트레이 이름으로 한 장 — 없으면 `undefined` */
export function trayOf(
  view: MonitoringTraysView | undefined,
  tray: TrayName,
): TrayView | undefined {
  return view?.trays.find((item) => item.tray === tray)
}
